import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Inbox } from "lucide-react";
import { AppShell, funderNav } from "@/shell/AppShell";
import {
  RightRail,
  RailSection,
  AttentionList,
  ActivityFeed,
  eventToActivityItem,
  NarrativeCard,
} from "@/design/components";
import { useSeed, useOrgIndex, useMoney, useActionPanelItems } from "@/domain/hooks";
import { toAttentionItems, topAttentionSeverity, shortAge } from "@/domain/actionPanel";
import { useStore } from "@/domain/store";

/** Per-destination heading. The sidebar owns navigation; no inline tab strip (spec §3.3). */
const HEADINGS: Record<string, { title: string; sub: string }> = {
  "": { title: "Portfolio", sub: "Programmes funded by the Danish Impact Fund. Read-only oversight with milestone disbursement approvals." },
  programmes: { title: "Programmes", sub: "Every programme the fund supports." },
  projects: { title: "Projects", sub: "Every project across the programmes shared with you." },
  approvals: { title: "Approvals", sub: "Routed milestone disbursements awaiting your decision." },
  insights: { title: "Insights", sub: "Portfolio pace, outlook and assurance." },
  reports: { title: "Reports", sub: "Oversight reporting from across the portfolio." },
  audit: { title: "Audit trail", sub: "Tamper-evident log of every action." },
};

function segFromPath(p: string): string {
  return p.replace(/^\/funder\/?/, "").split("/")[0];
}

export function FunderDashboardLayout() {
  const seed = useSeed();
  const orgs = useOrgIndex();
  const money = useMoney();
  const { pathname } = useLocation();
  const [railCollapsed, setRailCollapsed] = useState(false);
  const seg = segFromPath(pathname);
  const heading = HEADINGS[seg] ?? HEADINGS[""];

  // The action queue: milestone disbursements routed and waiting on the fund's decision.
  // Each row still leads with the tranche amount in the persona currency (EUR) - the
  // one fact this page has always shown - now sourced from the same ranked action-panel
  // feed every other persona's rail reads from, instead of a locally hard-coded filter.
  const { title: awaitingTitle, items: actionItems } = useActionPanelItems();
  const setWorkspace = useStore((s) => s.setWorkspace);
  const awaitingItems = toAttentionItems(actionItems, (it) =>
    it.category === "disbursement" && it.amount != null ? money(it.amount, "EUR") : shortAge(it.createdAt)
  ).map((it) => ({
    ...it,
    onClick: it.workspaceId ? () => setWorkspace(it.workspaceId!) : undefined,
  }));
  const awaitingSeverity = topAttentionSeverity(awaitingItems);

  return (
    <AppShell
      context="Danish Impact Fund"
      sections={funderNav}
      crumbs={[{ label: "Danish Impact Fund", to: "/funder" }, { label: heading.title }]}
      rightRail={
        <RightRail
          collapsed={railCollapsed}
          onToggle={() => setRailCollapsed((c) => !c)}
          summary={[{ id: "awaiting", count: awaitingItems.length, severity: awaitingSeverity }]}
        >
          <RailSection
            title={awaitingTitle}
            tone="action"
            icon={Inbox}
            count={awaitingItems.length}
            severity={awaitingSeverity}
          >
            <AttentionList items={awaitingItems} emptyLabel="No disbursements awaiting you." />
          </RailSection>
          <RailSection title="This week across your portfolio" divider>
            <NarrativeCard variant="briefing" title="Portfolio briefing" body={seed.briefing.whereWeAre} />
          </RailSection>
          <RailSection title="Latest disbursements">
            <ActivityFeed
              density="compact"
              items={seed.auditEvents.filter((e) => e.class === "approval").slice(0, 5).map((e) => eventToActivityItem(e, { meta: orgs[e.actorOrganisationId]?.name, amount: e.amount ? money(e.amount, "EUR") : undefined }))}
              viewAllHref="/funder/audit"
            />
          </RailSection>
        </RightRail>
      }
    >
      <div className="px-4 py-5 md:px-8 md:py-6">
        <div className="mb-5">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">{heading.title}</h1>
          <p className="mt-1 text-sm text-mute">{heading.sub}</p>
        </div>
        <Outlet />
      </div>
    </AppShell>
  );
}
